import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';

const LinkInsertions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "Indexed Articles",
      desc: "Placements within existing content that is already indexed and ranking.",
      icon: FiIcons.FiSearch
    },
    {
      title: "Contextual Integration",
      desc: "Natural link placement within relevant paragraphs and topics.",
      icon: FiIcons.FiAlignLeft
    },
    {
      title: "Traffic Targeting",
      desc: "We focus on pages that already receive consistent organic traffic.",
      icon: FiIcons.FiTrendingUp
    },
    {
      title: "Niche Relevance",
      desc: "Websites and articles closely aligned with your industry.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Structured Campaigns",
      desc: "A systematic approach to link acquisition for predictable growth.",
      icon: FiIcons.FiLayers
    }
  ];

  const benefits = [
    "Improve search engine rankings",
    "Drive targeted traffic",
    "Strengthen backlink profile",
    "Accelerate SEO results",
    "Complement guest posts",
    "Natural authority signals"
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* 1. First Section - Hero (H1) */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Link Insertions <span className="text-emerald-400">Services</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Place your brand within existing articles that already rank on Google and drive consistent traffic.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center w-full sm:w-auto"
            >
              <Button href="https://app.prboost.io/" variant="primary" className="!from-emerald-600 !to-teal-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(16,185,129,0.4)] w-full sm:w-auto">
                Start Campaign Now
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Build Authority with Existing Content (H2) */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="max-w-4xl mx-auto text-center mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Build Authority with Existing Content</h2>
            <div className="text-slate-400 space-y-8 text-xl leading-relaxed mx-auto">
              <p>
                Link insertions allow your brand to be added into articles that are already published, indexed, and performing in search engines. Instead of creating new content, this strategy places your link within relevant pages that already have authority and visibility.
              </p>
              <p>
                PRBoost helps businesses and agencies secure placements inside high-quality content, ensuring your brand is positioned naturally within trusted websites.
              </p>
            </div>
          </div>

          {/* 2. What This Service Includes (H2) */}
          <div className="max-w-6xl mx-auto mb-32">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center tracking-tight">What This Service Includes</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {includes.map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] p-8 rounded-3xl glass-card border-white/5 hover:border-emerald-500/20 transition-all group flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={item.icon} className="text-2xl" />
                  </div>
                  <h4 className="text-white font-bold text-xl mb-4">{item.title}</h4>
                  <p className="text-slate-400 leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why It Works - Centralized Callout */}
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-[3rem] p-12 md:p-20 border-emerald-500/20 bg-gradient-to-br from-slate-900/90 to-emerald-900/10 text-center">
              <h3 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Why Link Insertions Work</h3>
              <p className="text-xl md:text-2xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                Link insertions are effective because they leverage content that already has authority and visibility. Benefit from existing rankings rather than starting from scratch.
              </p>
              <div className="flex justify-center">
                <ul className="space-y-6 text-left inline-flex flex-col">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-5 text-slate-300 text-xl">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-emerald-400 text-2xl flex-shrink-0" />
                      <span className="font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 1. Last Section (H2) */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-emerald-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-extrabold text-white mb-8 tracking-tighter">Start Building Your <span className="text-emerald-400 text-gradient">Backlink Profile</span></h2>
          <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Link insertions provide a fast and effective way to build authority and improve your search visibility. Benefit from established traffic today.
          </p>
          <div className="flex justify-center">
            <Button href="https://app.prboost.io/" variant="primary" className="!from-emerald-600 !to-teal-600 !px-16 !py-6 text-2xl font-bold shadow-[0_0_60px_rgba(16,185,129,0.3)]">
              Create an Account
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LinkInsertions;
